import * as React from "react";
import { cn } from "./cn";
import { Container, type ContainerProps } from "./container";

type SectionTone = "paper" | "card" | "primary" | "transparent";

const tones: Record<SectionTone, string> = {
  paper: "bg-paper text-ink",
  card: "bg-card text-ink",
  primary: "bg-primary text-primary-fg",
  transparent: "",
};

// Scaled by `--density` (1 unless a brand-board look sets compact/airy), so
// the board's Spacing control is a token like every other look choice.
const spacing = {
  sm: "py-[calc(2rem*var(--density,1))] sm:py-[calc(2.5rem*var(--density,1))]",
  md: "py-[calc(3rem*var(--density,1))] sm:py-[calc(4rem*var(--density,1))]",
  lg: "py-[calc(4rem*var(--density,1))] sm:py-[calc(6rem*var(--density,1))]",
} as const;

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Background tone. @default "transparent" */
  tone?: SectionTone;
  /** Vertical rhythm. @default "md" */
  spacing?: keyof typeof spacing;
  /** Wrap children in a Container. Set false for full-bleed content. @default true */
  contained?: boolean;
  /** Container width when `contained`. @default "default" */
  width?: ContainerProps["width"];
}

/**
 * A vertically-padded page band with an optional background tone. Pair sections
 * with alternating tones (paper / card) to give long pages visual rhythm.
 */
export const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      className,
      tone = "transparent",
      spacing: space = "md",
      contained = true,
      width,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <section
        ref={ref}
        className={cn(tones[tone], spacing[space], className)}
        {...props}
      >
        {contained ? <Container width={width}>{children}</Container> : children}
      </section>
    );
  },
);
Section.displayName = "Section";
